import { X, Send, Instagram, Linkedin, Youtube, Facebook } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import PlaystoreBadge from '@/assets/playstore-badge.svg';
import IosBadge from '@/assets/appstore-badge.svg';
import { getDeferredInstallUrlForCurrentPage } from '@/utils/mobileApp';
import { useMemo } from 'react';
import { userUtils } from '@/utils/auth';
import { trackEvent } from '@/utils/analytics';
import { generateId } from '@/utils/idGenerator';

interface FooterLink {
  label: string;
  href: string;
  target?: string;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

const footerColumns: FooterColumn[] = [
  {
    title: 'Company',
    links: [
      { label: 'Home', href: '/' },
      { label: 'Blips', href: '/blips' },
      { label: 'Shop', href: '/shop' },
      { label: 'Live', href: '/live' },
    ],
  },
  {
    title: 'Website',
    links: [
      { label: 'About', href: 'https://www.auqli.live/about', target: '_blank' },
      { label: 'Blog', href: 'https://www.auqli.live/blog', target: '_blank' },
      { label: 'Tracxn', href: 'https://tracxn.com/d/companies/auqli/__YWV9ER2XafUm-niXc7KgRStFTQ_9iVeWgFtCjhvYH6Q', target: '_blank' },
      { label: 'Careers', href: 'https://www.auqli.live/careers', target: '_blank' },
      { label: 'Well found', href: 'https://wellfound.com/company/auqli', target: '_blank' },
      { label: 'CrunchBase', href: 'https://www.crunchbase.com/organization/auqli/', target: '_blank' },
      { label: 'Privacy Policy', href: 'https://www.auqli.live/privacy-policy', target: '_blank' },
      { label: 'Term Of Service', href: 'https://www.auqli.live/terms', target: '_blank' },
      { label: 'Contact & FAQs', href: 'https://www.auqli.live/contact', target: '_blank' },
    ],
  },
  {
    title: 'Quick Links',
    links: [
      { label: 'Help Center', href: '#' },
      { label: 'Events & Webinars', href: 'https://luma.com/user/auqliapp', target: '_blank' },
      { label: 'Become A Seller', href: 'https://www.auqli.live/sell',  target: '_blank' },
      { label: 'Shipping Policy', href: '#' },
      { label: 'Submit Feedback', href: '#' },
      { label: 'Auqli University', href: 'https://www.youtube.com/@auqliapp', target: '_blank' },
      { label: 'Join the Auqliverse', href: '#' },
    ],
  },
  {
    title: 'Industry',
    links: [
      { label: 'Fashion & Apparel', href: '/category/Men%20Bottoms' },
      { label: 'Tech & Gadgets', href: '/Portable%20Gadgets' },
      { label: 'Cosmetics', href: '/category/Skincare' },
      { label: 'Health & Wellness', href: '#' },
      { label: 'Home and Kitchen', href: '#' },
      { label: 'Baby & Kids', href: '/category/Kids%20Tablets' },
    ],
  },
  {
    title: 'Support',
    links: [{ label: 'Contact & FAQs', href: 'https://www.auqli.live/contact', target: '_blank' }],
  },
];

const socialLinks = [
  { icon: X, href: 'https://x.com/auqliapp', label: 'X (Twitter)' },
  { icon: Send, href: 'https://t.me/auqlinigeria', label: 'Telegram' },
  { icon: Instagram, href: 'https://www.instagram.com/auqliapp/#', label: 'Instagram' },
  { icon: Linkedin, href: 'https://www.linkedin.com/company/auqli/', label: 'LinkedIn' },
  { icon: Youtube, href: 'https://www.youtube.com/@auqliapp', label: 'YouTube' },
  { icon: Facebook, href: 'https://web.facebook.com/auqliapp/?_rdc=1&_rdr#', label: 'Facebook' },
];

export function Footer() {

    const androidInstallUrl = useMemo(() => getDeferredInstallUrlForCurrentPage('android', { itmSource: 'store' }), [])
    const iosInstallUrl = useMemo(() => getDeferredInstallUrlForCurrentPage('ios', { itmSource: 'store' }), [])

      // Retrieve user or provide a stable temporary session id
          const getUserOrTempSession = () => {
              const user = userUtils.getUser();
              if (user) return { user };
              const key = 'temp_session_id';
              let sid = localStorage.getItem(key);
              if (!sid) {
                  sid = generateId();
                  localStorage.setItem(key, sid);
              }
              return { tempSessionId: sid };
          }
    
      const handleBadgeClick = async (platform: 'android' | 'ios') => {
        const { user, tempSessionId } = getUserOrTempSession();
    
         await trackEvent('app download clicked', {
                  platform,
                  source: 'footer',
                  ...(user ? { userId: user.id, email: user.email, name: user.name } : { tempSessionId }),
              });
      };

  return (
    <footer className="w-full bg-black text-white">
      <div className="w-full max-w-[1445px] mx-auto px-5 lg:px-[30px] py-10 lg:py-[50px]">
        {/* Desktop: 5 columns, Mobile: Stacked */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-6 mb-10 lg:mb-[50px] lg:text-center">
          {footerColumns.map((column) => (
            <div key={column.title} className="flex flex-col gap-4 lg:gap-6 text-center lg:text-left">
              <h3 className="font-lexend font-normal text-[20px] leading-normal text-white">
                {column.title}
              </h3>
              <div className="flex flex-col gap-[10px] lg:gap-3 items-center lg:items-start">
                {column.links.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target={link.target || '_self'}
                    className="font-lexend font-light text-[16px] leading-[19.2px] text-[#BDBDBD] hover:text-white transition-colors capitalize"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>


        <div className="flex flex-col text-center lg:text-left lg:flex-row lg:items-center lg:justify-between gap-5 lg:gap-[30px] mb-10 lg:mb-[50px]">
          <div className="flex flex-col gap-[5px] justify-center lg:justify-start">
            <h3 className="font-lexend font-normal text-[20px] leading-normal text-white">
              Download our App
            </h3>
            <p className="font-lexend font-light text-[16px] leading-[28px] text-white lg:max-w-[521px]">
              Shop the way you love with Auqli. Download the app today and experience thrill!
            </p>
          </div>
          <div className="flex items-center justify-center gap-3">
            <a
              onClick={() => handleBadgeClick('android')}
              href={androidInstallUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get it on Google Play"
              className="h-[49px] w-auto"
            >
              <img src={PlaystoreBadge} alt="Get it on Google Play" className="h-full w-auto" />
            </a>
            <a
              onClick={() => handleBadgeClick('ios')}
              href={iosInstallUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download on the App Store"
              className="h-[49px] w-auto"
            >
              <img src={IosBadge} alt="Download on the App Store" className="h-full w-auto" />
            </a>
          </div>
        </div>


        <Separator className="bg-white/10 mb-10 lg:mb-[50px]" />


        <div className="flex justify-center mb-10 lg:mb-[50px]">
          <img
            src="/assets/auqli-logo.svg"
            alt="Auqli"
            className="w-[243px] h-[92px] text-white"
          />
        </div>

        {/* Copyright and Social Icons */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 lg:gap-0">
          <p className="font-lexend font-normal text-[12px] leading-[15px] tracking-[2px] uppercase text-white text-center lg:text-left">
            Copyright © 2025 Auqli Limited | All Rights Reserved
          </p>
          <div className="flex items-center justify-center lg:justify-end gap-2">
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="w-11 h-11 rounded-full bg-[#1A1A1A] flex items-center justify-center hover:bg-[#2A2A2A] transition-colors"
              >
                <social.icon className="w-5 h-5 text-white" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}